import React, { useState, useEffect } from 'react';
import { Key, Copy, Trash2, Plus, Code, BookOpen, TrendingUp, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DeveloperPortal({ user, onLogout }) {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyLimit, setNewKeyLimit] = useState(10000);
  const [createdKey, setCreatedKey] = useState(null);
  const [selectedTab, setSelectedTab] = useState('keys'); // keys, docs, examples

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/keys`, {
        withCredentials: true
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  };

  const createAPIKey = async () => {
    try {
      const response = await axios.post(`${API}/enterprise/keys`, {
        name: newKeyName,
        monthly_limit: newKeyLimit
      }, {
        withCredentials: true
      });

      setCreatedKey(response.data.api_key);
      fetchAPIKeys();
      setNewKeyName('');
      toast.success('API key created successfully');
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    }
  };

  const revokeAPIKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/enterprise/keys/${keyId}`, {
        withCredentials: true
      });
      fetchAPIKeys();
      toast.success('API key revoked');
    } catch (error) {
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (user?.subscription_status !== 'premium') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="max-w-4xl mx-auto text-center py-20">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">Premium Feature</h1>
          <p className="text-gray-600 mb-8">
            API access is only available for Premium subscribers. Upgrade to unlock enterprise features.
          </p>
          <Button onClick={() => window.location.href = '/dashboard'}>
            Upgrade to Premium
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_carinfo-lookup/artifacts/kwmtzzvb_image.png" 
                alt="IsItTaxed.co.uk" 
                className="h-12 w-auto cursor-pointer"
                onClick={() => window.location.href = '/dashboard'}
              />
              <span className="text-gray-400">|</span>
              <h1 className="text-xl font-bold text-gray-900">Developer Portal</h1>
            </div>
            <Button variant="outline" onClick={onLogout}>
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setSelectedTab('keys')}
            className={`px-6 py-3 font-semibold transition-colors ${
              selectedTab === 'keys'
                ? 'border-b-2 border-gray-900 text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Key className="inline h-4 w-4 mr-2" />
            API Keys
          </button>
          <button
            onClick={() => setSelectedTab('docs')}
            className={`px-6 py-3 font-semibold transition-colors ${
              selectedTab === 'docs'
                ? 'border-b-2 border-gray-900 text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <BookOpen className="inline h-4 w-4 mr-2" />
            Documentation
          </button>
          <button
            onClick={() => setSelectedTab('examples')}
            className={`px-6 py-3 font-semibold transition-colors ${
              selectedTab === 'examples'
                ? 'border-b-2 border-gray-900 text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Code className="inline h-4 w-4 mr-2" />
            Code Examples
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* API Keys Tab */}
        {selectedTab === 'keys' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">API Keys</h2>
                <p className="text-gray-600 mt-1">Manage your enterprise API access keys</p>
              </div>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gray-900 hover:bg-gray-800 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New Key
              </Button>
            </div>

            {/* API Keys List */}
            <div className="grid gap-4">
              {loading ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-gray-600">Loading API keys...</p>
                  </CardContent>
                </Card>
              ) : apiKeys.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No API keys yet</p>
                    <Button onClick={() => setShowCreateModal(true)}>
                      Create your first API key
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                apiKeys.map((key) => (
                  <Card key={key.id} className="border-2">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Key className="h-5 w-5 text-gray-600" />
                            <h3 className="text-lg font-bold">{key.name}</h3>
                            {!key.is_active && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                Revoked
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mb-3">
                            <code className="px-3 py-1 bg-gray-100 rounded text-sm font-mono">
                              {key.key_prefix}
                            </code>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600">Usage This Month</p>
                              <p className="font-semibold text-lg">
                                {key.calls_this_month.toLocaleString()} / {key.monthly_limit.toLocaleString()}
                              </p>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                <div
                                  className="bg-gray-900 h-2 rounded-full transition-all"
                                  style={{
                                    width: `${Math.min(
                                      (key.calls_this_month / key.monthly_limit) * 100,
                                      100
                                    )}%`
                                  }}
                                />
                              </div>
                            </div>
                            <div>
                              <p className="text-gray-600">Last Used</p>
                              <p className="font-semibold">
                                {key.last_used
                                  ? new Date(key.last_used).toLocaleDateString()
                                  : 'Never'}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Created {new Date(key.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          {key.is_active && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => revokeAPIKey(key.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Documentation Tab */}
        {selectedTab === 'docs' && (
          <div className="max-w-4xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">API Documentation</h2>
            
            <Card className="mb-6">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4">Authentication</h3>
                <p className="text-gray-600 mb-4">
                  All API requests require an API key. Include it in the <code className="px-2 py-1 bg-gray-100 rounded">X-API-Key</code> header.
                </p>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
{`curl https://isittaxed.co.uk/api/v1/vehicle/lookup \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"registration": "AB12CDE"}'`}
                </pre>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4">Endpoints</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-lg mb-2">POST /api/v1/vehicle/lookup</h4>
                    <p className="text-gray-600 mb-3">Look up a single vehicle by registration number</p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-semibold mb-2">Request Body:</p>
                      <pre className="bg-white p-3 rounded border text-sm overflow-x-auto">
{`{
  "registration": "AB12CDE"
}`}
                      </pre>
                      <p className="font-semibold mt-4 mb-2">Response:</p>
                      <pre className="bg-white p-3 rounded border text-sm overflow-x-auto">
{`{
  "registration": "AB12CDE",
  "make": "FORD",
  "model": "FOCUS",
  "colour": "BLUE",
  "tax_status": "Taxed",
  "tax_due_date": "2025-06-01",
  "mot_status": "Valid",
  "mot_expiry_date": "2025-08-15",
  "fuel_type": "Petrol",
  "year_of_manufacture": 2018
}`}
                      </pre>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-lg mb-2">POST /api/v1/vehicle/bulk-lookup</h4>
                    <p className="text-gray-600 mb-3">Look up multiple vehicles (max 100 per request)</p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-semibold mb-2">Request Body:</p>
                      <pre className="bg-white p-3 rounded border text-sm overflow-x-auto">
{`{
  "registrations": ["AB12CDE", "XY34ZZZ", "CD56EFG"]
}`}
                      </pre>
                      <p className="font-semibold mt-4 mb-2">Response:</p>
                      <pre className="bg-white p-3 rounded border text-sm overflow-x-auto">
{`{
  "total": 3,
  "successful": 2,
  "failed": 1,
  "results": [...],
  "errors": [
    {"registration": "XY34ZZZ", "error": "Vehicle not found"}
  ]
}`}
                      </pre>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4">Rate Limits</h3>
                <p className="text-gray-600">
                  Your API key has a monthly limit. You'll receive a <code className="px-2 py-1 bg-gray-100 rounded">429 Too Many Requests</code> response if you exceed your limit.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Code Examples Tab */}
        {selectedTab === 'examples' && (
          <div className="max-w-4xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Code Examples</h2>

            {/* JavaScript Example */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4">JavaScript / Node.js</h3>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`const response = await fetch('https://isittaxed.co.uk/api/v1/vehicle/lookup', {
  method: 'POST',
  headers: {
    'X-API-Key': 'your_api_key_here',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ registration: 'AB12CDE' })
});

const data = await response.json();
console.log(data);`}
                </pre>
              </CardContent>
            </Card>

            {/* Python Example */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4">Python</h3>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`import requests

response = requests.post(
    'https://isittaxed.co.uk/api/v1/vehicle/lookup',
    headers={'X-API-Key': 'your_api_key_here'},
    json={'registration': 'AB12CDE'}
)

data = response.json()
print(data)`}
                </pre>
              </CardContent>
            </Card>

            {/* Widget Example */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-4">Embeddable Widget</h3>
                <p className="text-gray-600 mb-4">
                  Add the vehicle checker to your website with just a few lines of code:
                </p>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`<div id="isittaxed-widget"></div>
<script src="https://isittaxed.co.uk/widget.js"></script>
<script>
  IsItTaxed.init({
    apiKey: 'your_api_key_here',
    container: '#isittaxed-widget',
    theme: 'light', // or 'dark'
    primaryColor: '#1f2937',
    size: 'medium' // small, medium, large
  });
</script>`}
                </pre>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6">
              <h3 className="text-2xl font-bold mb-4">Create API Key</h3>
              
              {createdKey ? (
                <div>
                  <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-4">
                    <p className="font-semibold text-yellow-900 mb-2">⚠️ Save this key securely!</p>
                    <p className="text-sm text-yellow-800">
                      You won't be able to see it again after closing this dialog.
                    </p>
                  </div>
                  
                  <div className="bg-gray-100 p-4 rounded-lg mb-4">
                    <code className="text-sm break-all">{createdKey}</code>
                  </div>
                  
                  <Button
                    onClick={() => copyToClipboard(createdKey)}
                    className="w-full mb-3"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy to Clipboard
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCreatedKey(null);
                      setShowCreateModal(false);
                    }}
                    className="w-full"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-semibold mb-2">
                      Key Name
                    </label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="e.g., Production API"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg"
                    />
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-semibold mb-2">
                      Monthly Limit
                    </label>
                    <input
                      type="number"
                      value={newKeyLimit}
                      onChange={(e) => setNewKeyLimit(parseInt(e.target.value))}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={createAPIKey}
                      disabled={!newKeyName}
                      className="flex-1 bg-gray-900 hover:bg-gray-800"
                    >
                      Create Key
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowCreateModal(false);
                        setNewKeyName('');
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default DeveloperPortal;
